import { useEffect } from "react";
import { useBridgeLotIncidents } from "../../hooks/useBridgeHistory";
import { formatDuration, fmtTime } from "../../lib/historyFormat";

interface BridgeLotIncidentsModalProps {
  lotId: string;
  lotName: string | null;
  siteKey: string;
  onClose: () => void;
}

/**
 * '문제 많은 주차장' 의 lot 한 건 클릭 시 그 lot 의 정체(DOWN) 이력을 보여주는 모달.
 * 데이터: GET /api/bridge-history/lots/{lotId}/incidents (최대 50건, 최신순).
 *
 * UX: ESC / 배경 클릭으로 닫힘. 모달 열려있을 동안 body 스크롤 차단.
 */
export default function BridgeLotIncidentsModal({
  lotId,
  lotName,
  siteKey,
  onClose,
}: BridgeLotIncidentsModalProps) {
  const { data, isLoading } = useBridgeLotIncidents(siteKey, lotId, 50);
  const incidents = data ?? [];
  const title = lotName?.trim() ? lotName : lotId;

  // ESC 닫기
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  // 모달 열려있는 동안 body 스크롤 차단
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`${title} 정체 이력`}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      onClick={onClose}
    >
      <div
        className="flex max-h-[80vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl border border-surface-border bg-surface shadow-xl dark:border-surface-darkBorder dark:bg-surface-darkSubtle"
        onClick={(e) => e.stopPropagation()}
      >
        {/* 헤더 */}
        <div className="flex items-center justify-between border-b border-surface-border px-5 py-3 dark:border-surface-darkBorder">
          <div className="flex flex-col">
            <span className="text-sm font-semibold text-slate-800 dark:text-slate-100">
              {title}
            </span>
            <span className="font-mono text-[11px] text-slate-400 dark:text-slate-500">
              {siteKey} · {lotId}
            </span>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600 dark:text-slate-500 dark:hover:bg-slate-800 dark:hover:text-slate-300"
            aria-label="닫기"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
              fill="currentColor"
              className="h-4 w-4"
            >
              <path d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" />
            </svg>
          </button>
        </div>

        {/* 본문 — incident 리스트 */}
        <div className="overflow-y-auto px-5 py-4">
          {isLoading && !data ? (
            <p className="py-6 text-center text-sm text-slate-500 dark:text-slate-400">
              불러오는 중…
            </p>
          ) : incidents.length === 0 ? (
            <p className="py-6 text-center text-sm text-slate-500 dark:text-slate-400">
              기록된 정체 구간이 없습니다.
            </p>
          ) : (
            <ul className="space-y-1.5">
              {incidents.map((i, idx) => (
                <li
                  key={`${i.startedAt}-${idx}`}
                  className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm"
                >
                  <span
                    aria-hidden="true"
                    className="inline-block h-2 w-2 rounded-full bg-status-majorOutage dark:bg-status-majorOutage-dark"
                  />
                  <span className="font-mono tabular-nums text-slate-700 dark:text-slate-300">
                    {fmtTime(i.startedAt)} → {fmtTime(i.endedAt)}
                  </span>
                  <span className="text-xs text-slate-500 dark:text-slate-400">
                    {i.ongoing ? (
                      <span className="font-medium text-rose-500">진행 중</span>
                    ) : (
                      formatDuration(i.durationSec)
                    )}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
